import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy } from "react";

import { TIMEFRAME_MS } from "@strat/format";
import type { BacktestResult, Candle, IndicatorSeries, RunDetail, Trade } from "@strat/format";
import { CHART_MARKERS_KEY, ChartFilters, useChartView } from "@strat/ui";
import type { ChartProps } from "@strat/ui";

import { api } from "../api";
import { Card } from "../shell/ui";

/**
 * A published backtest, drawn the way StrategyLab draws it: the candles it
 * was tested on with its indicators and every entry and exit, then the full
 * performance report — cumulative returns against buy & hold, drawdown,
 * monthly heatmap, rolling Sharpe, trade distribution. The lab's own
 * components (lazy, with Plotly), so a benchmark card and a Workbench run
 * are one visual language.
 */
const Chart = lazy(() =>
  import("../labPages").then((m) => ({ default: m.Chart as React.ComponentType<ChartProps> })),
);
const PerformanceReport = lazy(() =>
  import("../labPages").then((m) => ({
    default: m.PerformanceReport as React.ComponentType<{ result: BacktestResult; title?: string }>,
  })),
);

/** Stable while the result loads, so the chart's trade filter isn't redone every render. */
const NO_TRADES: Trade[] = [];

export function RunCharts({ runId }: { runId: string }) {
  // The full result (every trade, the analytics): the card page itself carries a thinned copy.
  const detail = useQuery({
    queryKey: ["hub-run-detail", runId],
    queryFn: () => api<RunDetail>(`/backtest/runs/${runId}`),
    staleTime: Infinity,
  });
  const d = detail.data;
  const from = d ? (d.params.from ?? d.result.equityCurve[0]?.time ?? null) : null;
  // The period's end is the end of its last day, often hours past the last
  // bar tested; the chart's presets count back from it, so bound it there.
  const lastBar = d?.result.equityCurve.at(-1)?.time;
  const lastBarEnd =
    d && lastBar !== undefined
      ? lastBar + ((TIMEFRAME_MS as Record<string, number>)[d.timeframe] ?? 0)
      : null;
  const to = d
    ? d.params.to === null || lastBarEnd === null
      ? (d.params.to ?? lastBarEnd)
      : Math.min(d.params.to, lastBarEnd)
    : null;
  const q = d
    ? `symbol=${d.symbol}&timeframe=${d.timeframe}&marketType=${d.marketType}${from ? `&from=${from}` : ""}${to ? `&to=${to}` : ""}`
    : "";
  const candles = useQuery({
    queryKey: ["hub-run-candles", runId],
    queryFn: () => api<{ candles: Candle[] }>(`/candles?${q}`).then((r) => r.candles),
    enabled: !!d,
    staleTime: Infinity,
  });
  const series = useQuery({
    queryKey: ["hub-run-series", runId],
    queryFn: () =>
      api<{ series: IndicatorSeries[] }>("/indicators/series", {
        method: "POST",
        body: {
          symbol: d!.symbol,
          timeframe: d!.timeframe,
          marketType: d!.marketType,
          defs: d!.spec.indicators,
          from,
          to,
        },
      }).then((r) => r.series),
    enabled: !!d && d.spec.indicators.length > 0,
    staleTime: Infinity,
  });

  // The same filter bar a live bot's chart has: signals, which trades, which layers.
  const chartView = useChartView(d?.result.trades ?? NO_TRADES);

  if (detail.isLoading)
    return (
      <Card title="Price & trades">
        <p className="muted">Loading the full result…</p>
      </Card>
    );
  if (!d) return null;
  return (
    <>
      <Card title="Price & trades">
        <ChartFilters state={chartView} indicators={d.spec.indicators} />
        {candles.data && candles.data.length > 1 ? (
          <Suspense fallback={<p className="muted">Loading chart…</p>}>
            <Chart
              candles={candles.data}
              indicatorSeries={series.data ?? []}
              indicators={d.spec.indicators}
              hiddenIds={chartView.hiddenIds}
              trades={chartView.shown}
              layers={chartView.view}
              symbol={d.symbol}
              timeframe={d.timeframe}
              marketType={d.marketType}
              height={480}
              dataStart={from ?? undefined}
              dataEnd={to ?? undefined}
            />
          </Suspense>
        ) : (
          <p className="muted">{candles.isLoading ? "Loading bars…" : "No bars for this window."}</p>
        )}
        <p className="muted small">
          {CHART_MARKERS_KEY} — {d.result.trades.length} trades over the tested window. Zoom with Ctrl +
          scroll, drag to pan.
        </p>
      </Card>
      <Suspense
        fallback={
          <Card title="Performance report">
            <p className="muted">Loading report…</p>
          </Card>
        }
      >
        <PerformanceReport result={d.result} title="Performance report" />
      </Suspense>
    </>
  );
}
